import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { tendersService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const STATUS_FLOW = ['Open', 'Submitted', 'Under Evaluation', 'Won', 'Lost'];

const STATUS_COLORS = {
  open: 'bg-primary/10 text-primary border-primary/20',
  submitted: 'bg-accent/10 text-accent border-accent/20',
  'under evaluation': 'bg-warning/10 text-warning border-warning/20',
  won: 'bg-success/10 text-success border-success/20',
  lost: 'bg-error/10 text-error border-error/20',
};

const TenderSubmissionModal = ({ tender, onClose, onUpdated }) => {
  const toast = useToast();
  const [status, setStatus] = useState(tender?.status || 'Open');
  const [bidAmount, setBidAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [fileName, setFileName] = useState('');
  const [saving, setSaving] = useState(false);

  if (!tender) return null;

  const canSubmit = status === 'Open';
  const nextStatus = canSubmit ? 'Submitted' : null;

  const handleSubmitBid = async () => {
    setSaving(true);
    try {
      await tendersService?.update(tender?.id, { tender_status: 'submitted' });
      setStatus('Submitted');
      toast?.success('Bid submitted successfully!');
      onUpdated?.();
    } catch (err) {
      toast?.error('Failed to submit bid');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      const dbStatus = newStatus?.toLowerCase()?.replace(' ', '_');
      await tendersService?.update(tender?.id, { tender_status: dbStatus });
      setStatus(newStatus);
      toast?.success(`Status updated to ${newStatus}`);
      onUpdated?.();
    } catch (err) {
      toast?.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) setFileName(file?.name);
  };

  const urgencyColor = tender?.daysLeft <= 3
    ? 'text-error bg-error/10 border-error/20'
    : tender?.daysLeft <= 7
    ? 'text-warning bg-warning/10 border-warning/20' :'text-slate-600 bg-slate-100 border-slate-200';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-24 py-20 border-b border-slate-100 bg-gradient-to-r from-warning/5 to-primary/5 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning to-primary flex items-center justify-center">
              <Icon name="FileText" size={18} className="text-white" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Tender Details</p>
              <h2 className="text-base font-bold text-slate-900 leading-tight truncate max-w-[260px]">{tender?.name}</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-24 space-y-20">
          {/* Status + Urgency */}
          <div className="flex flex-wrap gap-8">
            <span className={`px-10 py-5 rounded-lg text-xs font-semibold border ${STATUS_COLORS?.[status?.toLowerCase()] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
              {status}
            </span>
            {tender?.daysLeft !== null && (
              <span className={`px-10 py-5 rounded-lg text-xs font-semibold border flex items-center gap-6 ${urgencyColor}`}>
                <Icon name="Clock" size={11} />
                {tender?.daysLeft} days left
              </span>
            )}
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-12">
            {[
              { icon: 'Building2', label: 'Client', value: tender?.client || '—' },
              { icon: 'DollarSign', label: 'Tender Value', value: tender?.value || '—' },
              { icon: 'Calendar', label: 'Deadline', value: tender?.deadline || 'TBD' },
              { icon: 'Users', label: 'Technical Role', value: tender?.technicalRole || '—' },
            ]?.map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-14">
                <div className="flex items-center gap-8 mb-6">
                  <Icon name={item?.icon} size={13} className="text-slate-400" />
                  <span className="text-xs text-slate-500 font-medium">{item?.label}</span>
                </div>
                <p className="text-sm font-semibold text-slate-900">{item?.value}</p>
              </div>
            ))}
          </div>

          {/* Status Workflow */}
          <div>
            <h3 className="text-xs font-semibold text-slate-600 mb-10 flex items-center gap-8">
              <Icon name="GitBranch" size={13} className="text-slate-400" />
              Submission Workflow
            </h3>
            <div className="flex items-center gap-0">
              {STATUS_FLOW?.map((s, i) => (
                <React.Fragment key={s}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                      s === status
                        ? 'border-primary bg-primary'
                        : STATUS_FLOW?.indexOf(s) < STATUS_FLOW?.indexOf(status)
                        ? 'border-success bg-success' :'border-slate-200 bg-white'
                    }`}>
                      {STATUS_FLOW?.indexOf(s) < STATUS_FLOW?.indexOf(status) ? (
                        <Icon name="Check" size={10} className="text-white" />
                      ) : s === status ? (
                        <div className="w-3 h-3 rounded-full bg-white" />
                      ) : null}
                    </div>
                    <span className={`text-xs mt-4 font-medium whitespace-nowrap ${s === status ? 'text-primary' : 'text-slate-400'}`}>
                      {s}
                    </span>
                  </div>
                  {i < STATUS_FLOW?.length - 1 && (
                    <div className={`flex-1 h-px mx-4 mb-4 ${STATUS_FLOW?.indexOf(s) < STATUS_FLOW?.indexOf(status) ? 'bg-success' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Bid Submission Form — only when Open */}
          {status === 'Open' && (
            <div className="space-y-14 p-16 bg-primary/5 border border-primary/15 rounded-xl">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-8">
                <Icon name="Send" size={14} className="text-primary" />
                Submit Your Bid
              </h3>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-6">Bid Amount ($)</label>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e?.target?.value)}
                  placeholder="Enter your bid amount"
                  className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-6">Upload Documents</label>
                <label className="flex items-center gap-10 px-14 py-10 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all">
                  <Icon name="Upload" size={16} className="text-slate-400" />
                  <span className="text-sm text-slate-500">{fileName || 'Click to upload bid documents'}</span>
                  <input type="file" className="hidden" onChange={handleFileChange} accept=".pdf,.doc,.docx,.xlsx" />
                </label>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-6">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e?.target?.value)}
                  rows={2}
                  placeholder="Any additional notes for this submission..."
                  className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none"
                />
              </div>
            </div>
          )}

          {/* Status advance (for Submitted → Under Evaluation) */}
          {status === 'Submitted' && (
            <div className="p-14 bg-accent/5 border border-accent/15 rounded-xl">
              <p className="text-xs text-slate-600 mb-10">Move to next stage:</p>
              <button
                onClick={() => handleStatusChange('Under Evaluation')}
                disabled={saving}
                className="flex items-center gap-8 px-16 py-10 rounded-xl bg-accent/10 text-accent text-sm font-semibold border border-accent/20 hover:bg-accent/20 transition-all disabled:opacity-50"
              >
                <Icon name="ArrowRight" size={14} />
                Mark as Under Evaluation
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-12 px-24 py-16 border-t border-slate-100 bg-slate-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-white transition-colors"
          >
            Close
          </button>
          {status === 'Open' && (
            <button
              onClick={handleSubmitBid}
              disabled={saving}
              className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-warning to-primary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8"
            >
              {saving ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Submitting...</>
              ) : (
                <><Icon name="Send" size={16} /> Submit Bid</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenderSubmissionModal;
